import { createStandaloneToast } from "@chakra-ui/react";
import { useMemberAuthStore } from "modules/members/auth";
import { refresh } from "modules/members/api";
import { Channel, Socket } from "phoenix";
import { useEffect, useState } from "react";

const { toast } = createStandaloneToast();

export type SRSuccess<T> = {
  success: true;
  data: T;
  entity: {
    type: string;
    id?: string;
  };
};

type CleanupFn = () => void;
type SRFailure = {
  success: false;
  error: string;
  description?: string;
};
export type SR<T> = SRSuccess<T> | SRFailure;

const MEMBER_CONNECT_PATH = import.meta.env.DEV
  ? "ws://localhost:4000/members/socket"
  : (import.meta.env.VITE_WSS_ENDPOINT as string).replace(
      "/socket",
      "/members/socket",
    );

class MemberSocketsContainer {
  channels: Record<string, Channel>;
  channelConfigs: Record<
    string,
    {
      channelName: string;
      handlers: { msg: string; fn: (any) => any }[];
    }
  >;
  socket: Socket | undefined;
  memberId: string | undefined;
  subscriptions: { id: string; callback: () => any }[];
  connected: boolean;
  backoffReconnect: boolean;
  manualClosed: boolean;
  // Bumped by close() so reconnect work scheduled before it can tell it is
  // stale and must not resurrect a socket for a signed-out member.
  closeCount: number;
  messageQueue: any[];

  constructor() {
    this.socket = undefined;
    this.channels = {};
    this.memberId = undefined;
    this.channelConfigs = {};
    this.subscriptions = [];
    this.connected = false;
    this.backoffReconnect = false;
    this.manualClosed = false;
    this.closeCount = 0;
    this.messageQueue = [];
  }

  hasSocket = () => Boolean(this.socket);

  setMemberId = (memberId: string) => {
    this.memberId = memberId;
    return memberId;
  };

  setupSocket = (
    accessToken: string,
    getTokenFn: () => Promise<string>,
    callbackOnConnect: (connected: boolean) => any,
  ) => {
    if (!this.socket?.isConnected) {
      const socket = new Socket(MEMBER_CONNECT_PATH, {
        authToken: accessToken,
      });
      socket.connect();

      socket.onError((e) => {
        socket.disconnect();
        this.clearSocket();
        callbackOnConnect(false);
        this.slowReconnectCheck(getTokenFn, callbackOnConnect);
      });

      socket.onOpen(() => {
        this.connected = true;
        this.socket = socket;
        this.reconnectChannels();
        this.backoffReconnect = false;
        this.manualClosed = false;
        callbackOnConnect(true);
      });

      socket.onClose(() => {
        if (!this.manualClosed) {
          socket.disconnect();
          callbackOnConnect(false);
          this.clearSocket();
          this.slowReconnectCheck(getTokenFn, callbackOnConnect);
          this.connected = false;
        }
      });
    } else {
      callbackOnConnect(true);
    }
  };

  slowReconnectCheck = async (
    getTokenFn: () => Promise<string>,
    callbackOnConnect: (connected: boolean) => any,
  ) => {
    const closeCount = this.closeCount;
    const token = await getTokenFn();
    if (!token || closeCount !== this.closeCount) return;

    this.backoffReconnect = true;
    const time = document.hasFocus() ? 2_000 : 10_000;
    setTimeout(() => {
      if (closeCount !== this.closeCount) return;
      this.setupSocket(token, getTokenFn, callbackOnConnect);
    }, time);
  };

  onError = (fn) => this.socket?.onError(fn);

  teardownSocket = () => {
    if (this.socket) {
      this.socket.disconnect();
      this.clearSocket();
    }
  };

  close = () => {
    this.closeCount += 1;
    this.manualClosed = true;
    this.backoffReconnect = false;
    this.connected = false;
    this.channelConfigs = {};
    this.teardownSocket();
  };

  reconnectChannels = () => {
    Object.values(this.channelConfigs).map((config) => {
      if (!this.channels[config.channelName]) {
        const channel = this.addChannel(config.channelName);
        if (channel) {
          config.handlers.forEach(({ msg, fn }) => channel.on(msg, fn));
        }
      }
    });
  };

  getChannel = (channelName: string) => {
    return this.channels[channelName];
  };

  getChannelConfig = (channelName: string) => {
    return this.channelConfigs[channelName];
  };

  channelOn = (
    channelName: string,
    msg: string,
    fn: (a: any) => any,
    retries = 0,
  ): CleanupFn => {
    const channel = this.getOrCreateChannel(channelName);
    const config = this.channelConfigs[channelName];

    if (config) {
      this.channelConfigs[channelName] = {
        ...config,
        handlers: [...config.handlers, { msg, fn }],
      };
    }
    if (channel) {
      channel.on(msg, fn);
      return () => channel.off(msg);
    } else if (retries <= 20) {
      setTimeout(
        () => this.channelOn(channelName, msg, fn, retries + 1),
        retries * 100,
      );
      return () => null;
    } else {
      return () => null;
    }
  };

  getOrCreateChannel = (channelName: string) => {
    const c = this.channels[channelName];
    return c ?? this.addChannel(channelName);
  };

  removeChannel = (channelName: string) => {
    const channel = this.getChannel(channelName);
    if (!channel) {
      return;
    }

    delete this.channels[channelName];
    delete this.channelConfigs[channelName];

    channel.leave();
  };

  pushMessage = async <T extends any>(
    channelName: string,
    msg: string,
    payload?: Record<string, any>,
    retried: boolean = false,
  ): Promise<T> => {
    const channel = this.getChannel(channelName);
    if (channel) {
      return new Promise((resolve, reject) => {
        const send = channel.push(msg, payload ?? {});
        send.receive("ok", (r) => {
          if (r.success === false) {
            toast({
              status: "error",
              title: r.error,
              description: r.description,
            });

            return reject(r.error);
          } else {
            return resolve(r);
          }
        });
        send.receive("error", (e) => {
          toast({
            status: "error",
            title: "An error occurred",
          });
          return reject(e);
        });
        // Without this a push the server never answers leaves its caller
        // waiting forever. Callers decide what to tell the person.
        send.receive("timeout", () => reject("timeout"));
      });
    } else if (!retried) {
      this.addChannel(channelName);
      return this.pushMessage(channelName, msg, payload, true);
    } else {
      return Promise.reject("Failed to push message");
    }
  };

  addChannel = (channelName: string) => {
    if (!this.socket) return null;

    const channel = this.socket.channel(channelName, {});
    this.channels[channelName] = channel;
    if (!this.channelConfigs[channelName]) {
      this.channelConfigs[channelName] = {
        channelName,
        handlers: [],
      };
    }
    channel.join();

    channel.onClose(() => {
      delete this.channels[channelName];
      delete this.channelConfigs[channelName];
    });
    channel.onError((e) => {});
    return channel;
  };

  subscribeChanges = (id: string) => (notify: () => any) => {
    this.subscriptions = [{ id, callback: notify }, ...this.subscriptions];
    return () => {
      this.subscriptions = this.subscriptions.filter((s) => s.id !== id);
    };
  };

  clearSocket = () => {
    this.channels = {};
    this.socket = undefined;
  };
}

export const MemberSocket = new MemberSocketsContainer();

export const MemberSocketProvider = () => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const memberId = useMemberAuthStore((state) => state.id);
  const isSignedIn = useMemberAuthStore((state) => state.isSignedin);
  const [socketConnected, setSocketConnected] = useState(false);

  const getToken = async (): Promise<string> => {
    const result = await refresh();
    return result?.accessToken ?? "";
  };

  useEffect(() => {
    if (
      isSignedIn &&
      !socketConnected &&
      accessToken &&
      !MemberSocket.backoffReconnect
    ) {
      if (memberId) {
        MemberSocket.setMemberId(memberId);
      }
      MemberSocket.setupSocket(accessToken, getToken, setSocketConnected);
    }
  }, [accessToken, isSignedIn, memberId]);

  return null;
};
